import { Tooltip } from "antd";
import { Fragment } from "react";
import { useTranslation } from "react-i18next";
import { type DeclarationFieldsFragment } from "../gql/generated/graphqlSchema.tsx";
import { formatNumber } from "../utils/nutritionFormat.ts";

type DeclarationEntry = DeclarationFieldsFragment["entries"][number];

const lcFirst = (s: string, locale: string) => s.charAt(0).toLocaleLowerCase(locale) + s.slice(1);

const percentSuffix = (entry: DeclarationEntry, lang: string) =>
  entry.showQuantity && entry.percentage != null ? ` ${formatNumber(entry.percentage, lang, 0)}%` : "";

const RenderEntry = ({
  index,
  entries,
  uppercaseAllergens,
}: {
  readonly index: number;
  readonly entries: ReadonlyArray<DeclarationEntry>;
  readonly uppercaseAllergens: boolean;
}) => {
  const { t, i18n } = useTranslation();
  const entry = entries[index];
  const hasAllergens = entry.allergens.length > 0;
  const rawName = lcFirst(entry.name, i18n.language);
  const displayName =
    hasAllergens && uppercaseAllergens ? (
      rawName.toLocaleUpperCase(i18n.language)
    ) : hasAllergens ? (
      <strong>{rawName}</strong>
    ) : (
      rawName
    );
  const name = hasAllergens ? (
    <Tooltip
      destroyOnHidden
      title={`${t("allergens.title")}: ${entry.allergens
        .map((a) => t(`allergens.${a}`))
        .sort((a, b) => a.localeCompare(b, i18n.language))
        .join(", ")}`}
    >
      {displayName}
    </Tooltip>
  ) : (
    displayName
  );
  const pct = percentSuffix(entry, i18n.language);
  if (entry.childIndices.length > 0) {
    return (
      <>
        {name}
        {pct} (
        <DeclarationView entries={entries} indices={entry.childIndices} uppercaseAllergens={uppercaseAllergens} />)
      </>
    );
  }
  return (
    <span>
      {name}
      {pct}
    </span>
  );
};

// eslint-disable-next-line react/no-multi-comp
export const DeclarationView = ({
  indices,
  entries,
  uppercaseAllergens,
}: {
  readonly indices: ReadonlyArray<number>;
  readonly entries: ReadonlyArray<DeclarationEntry>;
  readonly uppercaseAllergens: boolean;
}) => (
  <>
    {indices.map((idx, i) => (
      <Fragment key={idx}>
        {i > 0 ? ", " : ""}
        <RenderEntry entries={entries} index={idx} uppercaseAllergens={uppercaseAllergens} />
      </Fragment>
    ))}
  </>
);
